import { BCX_setInterval, BCX_setTimeout, contextCurrentModArea, debugContextStart, debugMakeContextReport } from "./BCXContext";
import { VERSION, SUPPORTED_BC_VERSIONS } from "./config";
import { ModuleCategory, ModuleInitPhase } from "./constants";
import { moduleInitPhase } from "./moduleManager";
import { ConditionsGetCategoryData, ConditionsGetCategoryEnabled } from "./modules/conditions";
import { getDisabledModules } from "./modules/presets";
import { firstTimeInit, modStorage, modStorageSync } from "./modules/storage";
import { getPatchedFunctionsHashes } from "./patching";
import { detectOtherMods } from "./utilsClub";
import { crc32, isObject, measureDataSize } from "./utils";

import bcModSDK from "bondage-club-mod-sdk";

const MAX_STACK_SIZE = 15;
export const PROBLEMATIC_MESSAGE_SIZE = 180_000;

let firstError = true;

let lastReceivedMessageType = "";
let lastReceivedMessageTime = 0;
let lastSentMessageType = "";
let lastSentMessageTime = 0;

let logServerMessages = false;
export function debugSetLogServerMessages(value: boolean): void {
	logServerMessages = value;
}

export function debugGenerateReport(includeBCX: boolean = true): string {
	let res = `----- Debug report -----\n`;
	res += `Location: ${window.location.href.replace(/\d{4,}/g, "<numbers>")}\n`;
	res += `UA: ${window.navigator.userAgent}\n`;
	res += `BC Version: ${GameVersion}\n`;
	res += `ModSDK Version: ${bcModSDK.version}\n`;

	res += `Mods:\n` +
		bcModSDK.getModsInfo()
			.map(mod => `  - ${mod.fullName} (${mod.name}): ${mod.version}\n` + (mod.repository ? `    repository: ${mod.repository}\n` : ""))
			.join("");

	const otherMods = Object.entries(detectOtherMods()).filter(i => i[1]);
	if (otherMods.length > 0) {
		res += `Detected legacy mods (NOT USING ModSDK):\n` +
			otherMods
				.map(i => `  - ${i[0]}` + (typeof i[1] !== "boolean" ? `: ${i[1]}` : "") + "\n")
				.join("");
	} else {
		res += `No known legacy mods detected.\n`;
	}

	const now = Date.now();

	res += `\n----- BC state report -----\n`;
	res += `Mouse position: ${MouseX} ${MouseY}\n`;
	res += `Connected to server: ${ServerIsConnected}\n`;
	res += `Local time: ${now}\n`;
	res += `Server time: ${CurrentTime} (diff: ${(CurrentTime - now).toFixed(2)})\n`;
	res += `Screen: ${CurrentModule}/${CurrentScreen}\n`;
	res += `In chatroom: ${ServerPlayerIsInChatRoom()}\n`;
	res += `GLVersion: ${GLVersion}\n`;
	res += `Last received message: ${lastReceivedMessageType} (${lastReceivedMessageTime})\n`;
	res += `Last sent message: ${lastSentMessageType} (${lastSentMessageTime})\n`;

	if (includeBCX) {
		res += `\n----- BCX report -----\n`;
		res += `Version: ${VERSION}\n`;
		res += `Init state: ${ModuleInitPhase[moduleInitPhase]}\n`;
		res += `First init: ${firstTimeInit}\n`;
		res += `Disabled modules: ${getDisabledModules().map(i => ModuleCategory[i]).join(", ") || "[None]"}\n`;
		try {
			if (ConditionsGetCategoryEnabled("curses")) {
				res += `Curses: ${Object.keys(ConditionsGetCategoryData("curses").conditions).join(", ") || "[None]"}\n`;
			}
		} catch (error) {
			res += `ERROR getting Curses data: ${debugPrettifyError(error)}\n`;
		}
		try {
			if (ConditionsGetCategoryEnabled("rules")) {
				res += `Rules: ${Object.keys(ConditionsGetCategoryData("rules").conditions).join(", ") || "[None]"}\n`;
			}
		} catch (error) {
			res += `ERROR getting Rules data: ${debugPrettifyError(error)}\n`;
		}
	}

	// SDK report
	res += "\n" + debugGenerateSDKReport(includeBCX);

	return res;
}

export function debugGenerateSDKReport(verbose: boolean = false): string {
	let res = `----- ModSDK report -----\n`;
	const patchingInfo = Array.from(bcModSDK.getPatchingInfo().values());
	let hadWarnings = false;

	const overwrittenFunctions = patchingInfo.filter(fn => fn.currentEntrypoint !== fn.sdkEntrypoint);
	if (overwrittenFunctions.length > 0) {
		hadWarnings = true;
		res += `Functions overwritten by non-ModSDK mods:\n` +
			overwrittenFunctions.map(fn => `  - ${fn.name}: ${crc32(fn.currentEntrypoint?.toString().replaceAll("\r\n", "\n") ?? "")}\n`).join("");
	}

	if (!hadWarnings) {
		res += `No warnings.\n`;
	}

	const unexpectedHashes = getPatchedFunctionsHashes(false);
	if (unexpectedHashes.length > 0 && (verbose || SUPPORTED_BC_VERSIONS.includes(GameVersion))) {
		res += `\n----- BCX Patching report -----\n`;
		if (unexpectedHashes.length > 0) {
			res += `Patched functions with unknown checksums:\n` +
				unexpectedHashes.map(i => `  - ${i[0]}: ${i[1]}\n`).join("");
		}
	} else if (verbose) {
		res += `\n----- BCX Patching report -----\n`;
		res += `No warnings.\n`;
	}

	return res;
}

export function cleanupErrorLocation(location: string): string {
	return location
		.replaceAll(window.location.href.substring(0, window.location.href.lastIndexOf("/")), "<url>")
		.replace(/\/\d{4,}\.html/, "/<numbers>.html")
		.replace(/[?&]_=\d+(?=$|&|:)/, "");
}

export function debugPrettifyError(error: unknown): string {
	if (error instanceof Error) {
		let stack = `${error.stack}`.split("\n");
		if (stack.length > MAX_STACK_SIZE) {
			stack = stack.slice(0, MAX_STACK_SIZE).concat("    ...");
		}
		return stack.map(cleanupErrorLocation).join("\n");
	}
	return `${error}`;
}

function debugTransformThing(event: PromiseRejectionEvent): ErrorEvent {
	const info: ErrorEventInit = {
		message: "Unhandled promise rejection",
		error: event.reason,
	};
	if (event.reason instanceof Error) {
		info.message = `${event.reason.message} (unhandled rejection)`;

		// We're about to reconstruct filename:lineno:colno info from the first stack frame
		const stack = `${event.reason.stack}`.split("\n");
		const lastFrameLoc = stack[1]?.match(/\((.*):(\d+):(\d+)\)$/) ?? stack[0]?.match(/\((.*):(\d+):(\d+)\)$/) ?? [null, "", 0, 0];
		info.filename = lastFrameLoc[1];
		info.lineno = parseInt(lastFrameLoc[2], 10);
		info.colno = parseInt(lastFrameLoc[3], 10);
	}
	const error = new ErrorEvent("ErrorEvent", info);
	return error;
}

export function debugGenerateReportErrorEvent(event: ErrorEvent): string {
	const currentMod = contextCurrentModArea();

	let res = `----- UNHANDLED ERROR ${currentMod != null ? `(IN ${currentMod || "BC"}) ` : ""}-----\n` +
		`Message: ${event.message}\n` +
		`Source: ${cleanupErrorLocation(event.filename)}:${event.lineno}:${event.colno}\n`;

	res += debugPrettifyError(event.error) + "\n\n";

	res += debugMakeContextReport();

	try {
		res += "\n" + debugGenerateReport(currentMod === "BCX");
	} catch (error) {
		res += `----- Debug report -----\nERROR GENERATING DEBUG REPORT!\n${debugPrettifyError(error)}`;
	}

	return res;
}

export function debugGenerateReportManualError(description: string, error: unknown): string {
	const currentMod = contextCurrentModArea();

	let res = `----- ERROR ${currentMod != null ? `(IN ${currentMod || "BC"}) ` : ""}-----\n` +
		`Description: ${description}\n`;

	res += debugPrettifyError(error) + "\n\n";

	res += debugMakeContextReport();

	try {
		res += "\n" + debugGenerateReport(currentMod === "BCX");
	} catch (error2) {
		res += `----- Debug report -----\nERROR GENERATING DEBUG REPORT!\n${debugPrettifyError(error2)}`;
	}

	return res;
}

export function debugGenerateReportProblematicOutgoingMessage(messageType: string, message: unknown[]): string {
	const currentMod = contextCurrentModArea();

	let res = `----- Huge outgoing message report ${currentMod != null ? `(IN ${currentMod || "BC"}) ` : ""}-----\n`;

	res += `Message type: ${messageType}\n`;
	res += `Message total size: ${measureDataSize(message)}\n`;

	res += `\n----- Message size breakdown -----\n`;

	for (let i = 0; i < message.length; i++) {
		const part = message[i];
		res += `\n--- Part #${i} ---\n`;
		res += `Type: ${part == null ? "null" : Array.isArray(part) ? "array" : typeof part}\n`;
		res += `Size: ${measureDataSize(part)}\n`;

		if (typeof part === "string" && part.length < 64) {
			res += `Value: ${JSON.stringify(part)}\n`;
		}

		if (isObject(part)) {
			res += "Breakdown by keys:\n";
			for (const [key, value] of Object.entries(part).sort((a, b) => measureDataSize(b[1]) - measureDataSize(a[1]))) {
				res += `- ${key}: ${measureDataSize(value)}\n`;
				if (isObject(value)) {
					for (const [key2, value2] of Object.entries(value).sort((a, b) => measureDataSize(b[1]) - measureDataSize(a[1]))) {
						res += `  - ${key2}: ${measureDataSize(value2)}\n`;
					}
				}
			}
		}
	}

	res += "\n";

	res += debugMakeContextReport();

	try {
		res += "\n" + debugGenerateReport(currentMod === "BCX");
	} catch (error2) {
		res += `----- Debug report -----\nERROR GENERATING DEBUG REPORT!\n${debugPrettifyError(error2)}`;
	}

	return res;
}

export function showErrorOverlay(
	title: string,
	description: string,
	contents: string,
	wrapCodeBlock: boolean = true,
	minTimeout?: number,
	preContentHook?: (win: HTMLDivElement) => void
): void {
	console.info("Error overlay displayed\n", contents);

	if (wrapCodeBlock) {
		contents = "```\n" + contents.trim() + "\n```";
	}

	const overlay = document.createElement("div");
	overlay.style.position = "fixed";
	overlay.style.inset = "0px";
	overlay.style.zIndex = "999999";
	overlay.style.background = "#00000090";

	// Nice window
	const win = document.createElement("div");
	overlay.appendChild(win);
	win.style.position = "absolute";
	win.style.inset = "5%";
	win.style.background = "white";
	win.style.display = "flex";
	win.style.flexDirection = "column";
	win.style.padding = "1em";
	win.style.overflow = "auto";

	// Title
	const titleElem = document.createElement("h1");
	win.appendChild(titleElem);
	titleElem.innerText = title;

	// Description
	const descriptionElement = document.createElement("p");
	win.appendChild(descriptionElement);
	descriptionElement.innerHTML = description;

	const contentElem = document.createElement("textarea");

	// Copy button
	if (preContentHook) {
		preContentHook(win);
	} else {
		const copy = document.createElement("button");
		copy.style.cursor = "pointer";
		win.appendChild(copy);
		copy.innerText = "Copy report";

		copy.onclick = () => {
			contentElem.focus();
			contentElem.select();

			if (navigator.clipboard) {
				navigator.clipboard.writeText(contentElem.value);
			} else {
				try {
					// eslint-disable-next-line @typescript-eslint/no-deprecated
					document.execCommand("copy");
				} catch (err) {
					/* Ignore */
				}
			}
		};
	}

	// Content
	win.appendChild(contentElem);
	contentElem.readOnly = true;
	contentElem.value = contents;
	contentElem.style.minHeight = "min(10em, 50vh)";
	contentElem.style.flex = "1";
	contentElem.style.margin = "0.5em 0";

	// Close button
	let timeout = minTimeout ?? 0;
	const close = document.createElement("button");
	win.appendChild(close);
	close.onclick = () => {
		if (timeout > 0)
			return;
		overlay.remove();
	};
	const updateCloseButton = () => {
		close.innerText = timeout > 0 ? `Close (${timeout})` : "Close";
		close.disabled = timeout > 0;
		if (timeout > 0) {
			BCX_setTimeout(() => {
				timeout--;
				updateCloseButton();
			}, 1000);
		} else {
			close.style.cursor = "pointer";
		}
	};
	updateCloseButton();

	// Display it
	(document.activeElement as HTMLElement)?.blur?.();
	window.document.body.appendChild(overlay);
}

let compatibilityCheckTimeout: number | undefined;
const COMPATIBILITY_CHECK_INTERVAL = 60_000;
let didReportCompatibilityIssues = false;

export function detectCompatibilityProblems(): void {
	if (didReportCompatibilityIssues || moduleInitPhase !== ModuleInitPhase.ready)
		return;

	const legacyMods = detectOtherMods();
	let result = "";
	let wait = 0;

	if (legacyMods.BcUtil) {
		wait = 5;
		result += `----- BC-Util -----\n` +
			`BCX detected the presence of the incompatible mod BC-Util.\n` +
			`BC-Util is a useful and high quality mod, which has unfortunately not been updated since September 2021 and doesn't use ModSDK.\n` +
			`It isn't compatible with BCX due to that and is known to cause problems, including crashes (especially in the wardrobe).\n` +
			`\n`;
	}

	if (legacyMods.QuickAccessMenu) {
		wait = 5;
		result += `----- Quick Access Menu (QAM) -----\n` +
			`BCX detected the presence of the incompatible mod Quick Access Menu.\n` +
			`This mod is deprecated and is known to conflict with other mods (e.g. BCX or FBC). Continued usage of this mod is not advised.\n` +
			`Contact its author for more information.\n` +
			`Error reports while using QAM will not be acted upon.\n` +
			`\n`;
	}

	if (legacyMods.Curse) {
		result += `----- "Cursed" Script -----\n` +
			`BCX detected the presence of the obsolete mod Cursed script.\n` +
			`Curse is the spiritual predecessor of BCX, and can be considered obsolete since BCX has almost the same features (and much more). Curse is no longer updated since September 2021 and doesn't use ModSDK.\n` +
			`As BCX is meant to supersede Curse, no compatibility can be guaranteed.\n` +
			`\n`;
	}

	const patchingInfo = Array.from(bcModSDK.getPatchingInfo().values());
	const unexpectedHashes = SUPPORTED_BC_VERSIONS.includes(GameVersion) ? getPatchedFunctionsHashes(false) : [];
	const overwrittenFunctions = patchingInfo.filter(fn => fn.currentEntrypoint !== fn.sdkEntrypoint);

	// If no known legacy mods are detected, look for unknown ones where it matters
	if (Array.from(Object.values(legacyMods)).every(v => !v) && (unexpectedHashes.length > 0 || overwrittenFunctions.length > 0)) {
		result += `----- Unknown mod not using ModSDK -----\n` +
			`BCX detected the presence of modifications not done using ModSDK or any known legacy mod.\n` +
			`If you are not author of the mod, please report what mod caused this warning on the BC Scripting Community Discord server: https://discord.gg/SHJMjEh9VH\n` +
			`If you are author of the mod triggering this warning, please modify your mod to use ModSDK: https://github.com/Jomshir98/bondage-club-mod-sdk. Feel free to ask for help doing that on the above-mentioned Discord server.\n` +
			`\n`;

	}

	// If there is a result, attach extra info to trace it
	if (result) {
		result += `----- Detected modifications -----\n` +
			(unexpectedHashes.length > 0 ? `Patched functions with unknown checksums:\n` + unexpectedHashes.map(i => `  - ${i[0]}: ${i[1]}\n`).join("") : "") +
			(overwrittenFunctions.length > 0 ? `Overwritten functions:\n` + overwrittenFunctions.map(fn => `  - ${fn.name}: ${crc32(fn.currentEntrypoint?.toString().replaceAll("\r\n", "\n") ?? "")}\n`).join("") : "") +
			`\n`;
	}

	if (result) {
		// Checksum the result and check against already seen one
		const checksum = crc32(result);
		if (modStorage.compatibilityCheckerWarningIgnore === checksum)
			return;

		didReportCompatibilityIssues = true;
		showErrorOverlay(
			"BCX Compatibility checker",
			"BCX's Compatibility checker detected problems with other mods you appear to be using.<br />" +
			"For reasons stated below please reconsider using mentioned mods.<br />" +
			"Please note, that this is a warning for you, not meant to be reported. It says that some features are likely to be broken.<br />" +
			"If you have any questions or think this message is an error, please get in touch with us on <a href='https://discord.gg/SHJMjEh9VH' target='_blank'>BC Scripting Community</a> Discord server.<br />" +
			"You can use the 'Close' button at the bottom to continue anyway.",
			result + `Report signature: ${checksum}`,
			false,
			wait,
			(win) => {
				const doNotShowAgain = document.createElement("button");
				doNotShowAgain.style.cursor = "pointer";
				win.appendChild(doNotShowAgain);
				doNotShowAgain.innerText = "Do not show this report again unless something changes";

				doNotShowAgain.onclick = () => {
					doNotShowAgain.innerText = "This report won't show again unless something changes.";
					doNotShowAgain.disabled = true;
					modStorage.compatibilityCheckerWarningIgnore = checksum;
					modStorageSync();
				};
			}
		);
	} else if (modStorage.compatibilityCheckerWarningIgnore != null) {
		delete modStorage.compatibilityCheckerWarningIgnore;
		modStorageSync();
	}
}

const sourceBasedErrorMessage = {
	bcx: "<br /><h3>Whoops... seems like BCX might be to blame this time</h3> Could you please help us by submitting the report below to the <a href='https://discord.gg/SHJMjEh9VH' target='_blank'>BC Scripting Community Discord</a> server?<br />Thank you!</p>",
	knownMod: (mod: string) => `<br /><h3>The error seems to come from mod ${mod}</h3> Please submit the report to <a href='https://discord.gg/SHJMjEh9VH' target='_blank'>BC Scripting Community Discord</a> server!`,
	bc: "<br /><h3>The error seems not to come from any ModSDK mod!</h3> Please submit the report to <a href='https://discord.gg/dkWsEjf' target='_blank'>Bondage Club's Discord</a> server!",
	unknown: "<br /><h3>Could not detect origin of the error.</h3> Please submit the report to <a href='https://discord.gg/dkWsEjf' target='_blank'>Bondage Club's Discord</a> server!",
} as const;

export function onUnhandledError(event: ErrorEvent) {
	if (!firstError)
		return;
	firstError = false;
	const currentMod = contextCurrentModArea();
	// Display error window
	showErrorOverlay(
		"Crash Handler (by ModSDK)",
		"The Crash Handler provided by ModSDK detected an uncaught error, which most likely crashed the Bondage Club.<br />" +
		"While reporting this error, please use the information below to help us find the source faster.<br />" +
		"You can use the 'Close' button at the bottom to continue, however BC may no longer work correctly until you reload the current tab." +
		(
			currentMod === "BCX" ? sourceBasedErrorMessage.bcx :
				currentMod === "" ? sourceBasedErrorMessage.bc :
					currentMod == null ? sourceBasedErrorMessage.unknown :
						sourceBasedErrorMessage.knownMod(currentMod)
		),
		debugGenerateReportErrorEvent(event)
	);
}

export function onUnhandledRejection(event: PromiseRejectionEvent) {
	if (!firstError)
		return;
	firstError = false;
	const currentMod = contextCurrentModArea();
	// Display error window
	showErrorOverlay(
		"Crash Handler (by ModSDK)",
		"The Crash Handler provided by ModSDK detected an unhandled promise rejection error, which most likely crashed the Bondage Club.<br />" +
		"While reporting this error, please use the information below to help us find the source faster.<br />" +
		"You can use the 'Close' button at the bottom to continue, however BC may no longer work correctly until you reload the current tab." +
		(
			currentMod === "BCX" ? sourceBasedErrorMessage.bcx :
				currentMod === "" ? sourceBasedErrorMessage.bc :
					currentMod == null ? sourceBasedErrorMessage.unknown :
						sourceBasedErrorMessage.knownMod(currentMod)
		),
		debugGenerateReportErrorEvent(debugTransformThing(event))
	);
}

export function reportManualError(description: string, error: unknown) {
	console.error(`BCX: Error report: ${description}\n`, error);
	if (!firstError)
		return;
	firstError = false;
	const currentMod = contextCurrentModArea();
	// Display error window
	showErrorOverlay(
		"Error Report (by BCX)",
		"The following error happend in event originating from BCX.<br />" +
		"While reporting this error, please use the information below to help us find the source faster.<br />" +
		"You can use the 'Close' button at the bottom to continue, however BC may no longer work correctly until you reload the current tab." +
		(
			currentMod === "BCX" ? sourceBasedErrorMessage.bcx :
				currentMod === "" ? sourceBasedErrorMessage.bc :
					currentMod == null ? sourceBasedErrorMessage.unknown :
						sourceBasedErrorMessage.knownMod(currentMod)
		),
		debugGenerateReportManualError(description, error)
	);
}

export function reportProblematicOutgoingMessage(messageType: string, message: unknown[]) {
	const totalSize = measureDataSize(message);

	console.error(`BCX: Outgoing message size: ${totalSize}`, "\nIndividual sizes:", message.map(measureDataSize), "\nMessage:", message);
	if (!firstError)
		return;
	firstError = false;

	// Display error window
	showErrorOverlay(
		"Profile data size alert",
		"BCX detected a large outgoing data packet that might cause you to disconnect from the server.<br />" +
		"The most common cause for this is too much data being stored by all of your mods/extensions together. This problem won't be fixed by simply disabling some or all extensions.<br />" +
		"Solution to this problem isn't straightforward - we recommend asking for help on the <a href='https://discord.gg/dkWsEjf' target='_blank'>Bondage Club's Discord</a> server.<br />" +
		"<h3>If you do encounter a disconnect:</h3> Please submit the report to <a href='https://discord.gg/dkWsEjf' target='_blank'>Bondage Club's Discord</a> server!<br />" +
		"<i>This message will not show again during this session.</i>",
		debugGenerateReportProblematicOutgoingMessage(messageType, message)
	);
}

function checkOutgoingMessageSize(messageType: string, message: unknown[]): void {
	const size = measureDataSize(message);
	if (size > 0 && size > PROBLEMATIC_MESSAGE_SIZE) {
		reportProblematicOutgoingMessage(messageType, message);
	}
}

// Server message origin
let originalSocketEmitEvent: undefined | ((...args: any[]) => any);
function bcxSocketEmitEvent(this: any, ...args: any[]) {
	const message = Array.isArray(args[0]) && typeof args[0][0] === "string" ? args[0][0] : "[unknown]";
	lastReceivedMessageType = message;
	lastReceivedMessageTime = Date.now();

	const parameters = Array.isArray(args[0]) ? args[0].slice(1) : [];
	if (logServerMessages) {
		console.log("\u2B07 Receive", message, ...parameters);
	}

	const ctx = debugContextStart(`Server message ${message}`, {
		root: true,
		modArea: "",
		extraInfo() {
			return `Event: ${message}\n` + parameters.map(i => JSON.stringify(i, undefined, "  ")).join("\n");
		},
	});
	const res = originalSocketEmitEvent?.apply(this, args);
	ctx.end();
	return res;
}

// Server outgoing message
let originalSocketEmit: undefined | ((...args: any[]) => any);
function bcxSocketEmit(this: any, ...args: unknown[]) {
	const messageType = Array.isArray(args) && typeof args[0] === "string" ? args[0] : "[unknown]";
	lastSentMessageType = messageType;
	lastSentMessageTime = Date.now();
	if (logServerMessages) {
		console.log("\u2B06 Send", ...args);
	}
	if (args.length >= 2) {
		checkOutgoingMessageSize(messageType, args);
	}
	return originalSocketEmit?.apply(this, args);
}

// Click origin
let originalClick: undefined | ((event: PointerEvent) => void);
function bcxClick(this: any, event: PointerEvent) {
	const ctx = debugContextStart(`Canvas click`, {
		root: true,
		modArea: "",
		extraInfo: () => `X: ${MouseX}\nY: ${MouseY}`,
	});
	const res = originalClick?.call(this, event);
	ctx.end();
	return res;
}

let originalRAF: Window["requestAnimationFrame"] | undefined;
function bcxRaf(this: any, fn: FrameRequestCallback): number {
	return originalRAF?.call(this, (...rafArgs: [DOMHighResTimeStamp]) => {
		const ctx = debugContextStart(`Animation frame`, {
			root: true,
			modArea: "",
			extraInfo: () => `time: ${rafArgs}`,
		});
		const res = fn.apply(window, rafArgs);
		ctx.end();
		return res;
	}) ?? 0;
}

export function InitErrorReporter() {
	window.addEventListener("error", onUnhandledError);
	window.addEventListener("unhandledrejection", onUnhandledRejection);
	// Server message origin
	if (originalSocketEmitEvent === undefined && typeof (ServerSocket as any)?.__proto__?.emitEvent === "function") {
		originalSocketEmitEvent = (ServerSocket as any).__proto__.emitEvent;
		(ServerSocket as any).__proto__.emitEvent = bcxSocketEmitEvent;
	}
	// Server outgoing message
	if (originalSocketEmit === undefined && typeof (ServerSocket as any)?.__proto__?.emit === "function") {
		originalSocketEmit = (ServerSocket as any).__proto__.emit;
		(ServerSocket as any).__proto__.emit = bcxSocketEmit;
	}

	const canvas = document.getElementById("MainCanvas") as (HTMLCanvasElement | undefined);
	if (canvas) {
		// Click origin
		if (originalClick === undefined && typeof canvas.onclick === "function") {
			originalClick = canvas.onclick;
			canvas.onclick = bcxClick;
		}
	}

	// Frame origin
	if (originalRAF === undefined && typeof window.requestAnimationFrame === "function") {
		originalRAF = window.requestAnimationFrame;
		window.requestAnimationFrame = bcxRaf;
	}

	if (compatibilityCheckTimeout == null) {
		compatibilityCheckTimeout = BCX_setInterval(() => {
			detectCompatibilityProblems();
		}, COMPATIBILITY_CHECK_INTERVAL);
		BCX_setTimeout(() => {
			detectCompatibilityProblems();
		}, 3_000);
	}
}

export function UnloadErrorReporter() {
	window.removeEventListener("error", onUnhandledError);
	// Server message origin
	if (originalSocketEmitEvent && (ServerSocket as any).__proto__.emitEvent === bcxSocketEmitEvent) {
		(ServerSocket as any).__proto__.emitEvent = originalSocketEmitEvent;
		originalSocketEmitEvent = undefined;
	}
	// Server outgoing message
	if (originalSocketEmit && (ServerSocket as any).__proto__.emit === bcxSocketEmit) {
		(ServerSocket as any).__proto__.emit = originalSocketEmit;
		originalSocketEmit = undefined;
	}
	const canvas = document.getElementById("MainCanvas") as (HTMLCanvasElement | undefined);
	// Click origin
	if (canvas && originalClick && canvas.onclick === bcxClick) {
		canvas.onclick = originalClick;
		originalClick = undefined;
	}
	// Frame origin
	if (originalRAF && window.requestAnimationFrame === bcxRaf) {
		window.requestAnimationFrame = originalRAF;
		originalRAF = undefined;
	}

	if (compatibilityCheckTimeout != null) {
		clearInterval(compatibilityCheckTimeout);
		compatibilityCheckTimeout = undefined;
	}
}
